import { useState } from "react";

import { Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { useHead } from "@unhead/react";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box, Flex } from "@/components/layout/flexbox";
import { PublicFooter } from "@/components/nav/PublicFooter";
import { PublicNav } from "@/components/nav/PublicNav";
import { Container } from "@/components/pieces/bootstrap";
import {
  StyledSubtitle,
  StyledTitle,
} from "@/components/pieces/marketing/Titles";
import {
  HEADING_FONT,
  PURPLE_TEXT_COLOR,
  SITE_NAME,
  STANDARD_NAV_HEIGHT,
} from "@/css/constants";

const StyledAccordionButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${HEADING_FONT};
  font-size: 20px;
  font-weight: 600;
  text-align: left;
`;

const StyledAccordionContent = styled(Box)<{ isOpen?: boolean }>`
  overflow: hidden;
  transition: all 300ms ease-in-out;
  padding: 0 24px;
  padding-bottom: ${(props) => (props.isOpen ? "24px" : "0")};
  max-height: ${(props) => (props.isOpen ? "500px" : "0")};
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
`;

const InfoCard = styled(Box)`
  background-color: #fff;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  padding: 24px;
  width: ${(props) => props.width || "100%"};
`;

const ProcessCard = styled(Box)`
  background-color: #fff;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  padding: 24px;
`;

const Dot = styled(Box)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #3788ee;
  margin-right: 16px;
  margin-top: 8px;
  flex-shrink: 0;
`;

interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

function AccordionItem({
  title,
  children,
  isOpen,
  onToggle,
}: AccordionItemProps) {
  return (
    <Box
      backgroundColor="#FFF"
      mb={3}
      style={{ border: "1px solid #edf0f5", borderRadius: 8 }}
    >
      <StyledAccordionButton onClick={onToggle}>
        {title}
        <Box fontSize={24} color={PURPLE_TEXT_COLOR}>
          {isOpen ? "−" : "+"}
        </Box>
      </StyledAccordionButton>
      <StyledAccordionContent isOpen={isOpen}>
        <Box fontSize={18} lineHeight="160%">
          {children}
        </Box>
      </StyledAccordionContent>
    </Box>
  );
}

export function EnterpriseAI() {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [selectedProcess, setSelectedProcess] = useState<number | null>(null);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const dataFlowSteps = [
    {
      title: "Input Processing",
      icon: <Icon icon="shield" />,
      details: "Secure web interface with TLS 1.2+ encryption",
    },
    {
      title: "AI Processing",
      icon: <Icon icon="database" />,
      details: "Secure integrations with top-tier cloud providers",
    },
    {
      title: "Data Storage",
      icon: <Icon icon="key" />,
      details: "Encrypted storage with logical workspace isolation",
    },
  ];

  useHead({
    bodyAttrs: { class: "public-site" },
  });

  return (
    <>
      <SEOHead
        title={`Enterprise AI Security & Privacy | ${SITE_NAME}`}
        description="Our commitment to protecting your data while delivering powerful AI capabilities."
        canonicalPath="/enterprise-privacy"
      />
      <PublicNav />
      <Box mt={STANDARD_NAV_HEIGHT}>
        <Box
          style={{
            background: "linear-gradient(90deg, #09265F 0%, #0B3689 100%)",
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          p={["50px 0", "50px 0", "100px 0"]}
        >
          <Container className="!max-w-marketing-content">
            <Flex flexDirection="column" alignItems="center">
              <Box maxWidth={720}>
                <StyledTitle className="text-white">
                  Enterprise Security & Privacy
                </StyledTitle>
              </Box>
              <StyledSubtitle className="text-white/80">
                Our commitment to protecting your data and maintaining security
                while delivering powerful AI capabilities
              </StyledSubtitle>
            </Flex>
          </Container>
        </Box>

        <Box backgroundColor="#F6F6FB" py={90}>
          <Container className="!max-w-marketing-content">
            <Flex flexDirection="column" alignItems="center">
              <StyledTitle>Our Commitments</StyledTitle>
              <StyledSubtitle>
                Enterprise-grade security and privacy by design
              </StyledSubtitle>
              <Flex mt={4} flexWrap="wrap" justifyContent="space-between">
                <InfoCard width={["100%", "100%", "32%"]}>
                  <Box
                    fontFamily={HEADING_FONT}
                    fontSize={24}
                    fontWeight={700}
                    color={PURPLE_TEXT_COLOR}
                  >
                    Data Ownership
                  </Box>
                  <Box mt={4}>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>
                        Customer data never used for AI training or model
                        improvement
                      </Box>
                    </Flex>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Logical isolation of all customer data</Box>
                    </Flex>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>
                        Customer-controlled data retention periods
                      </Box>
                    </Flex>
                  </Box>
                </InfoCard>

                <InfoCard width={["100%", "100%", "32%"]}>
                  <Box
                    fontFamily={HEADING_FONT}
                    fontSize={24}
                    fontWeight={700}
                    color={PURPLE_TEXT_COLOR}
                  >
                    Security
                  </Box>
                  <Box mt={4}>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Encryption for sensitive data at rest</Box>
                    </Flex>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>
                        TLS 1.2+ encryption for all data in transit
                      </Box>
                    </Flex>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>SOC 2 Type II and SOC 3 certified</Box>
                    </Flex>
                  </Box>
                </InfoCard>

                <InfoCard width={["100%", "100%", "32%"]}>
                  <Box
                    fontFamily={HEADING_FONT}
                    fontSize={24}
                    fontWeight={700}
                    color={PURPLE_TEXT_COLOR}
                  >
                    Control
                  </Box>
                  <Box mt={4}>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>SAML SSO integration</Box>
                    </Flex>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Role-based access controls (RBAC)</Box>
                    </Flex>
                    <Flex mb={3} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>
                        Comprehensive audit logging and monitoring
                      </Box>
                    </Flex>
                  </Box>
                </InfoCard>
              </Flex>
            </Flex>
          </Container>
        </Box>

        <Box backgroundColor="#FFF" py={90}>
          <Container className="!max-w-marketing-content">
            <Flex flexDirection="column" alignItems="center">
              <StyledTitle>System Architecture</StyledTitle>
              <StyledSubtitle>
                Secure, scalable, and enterprise-ready
              </StyledSubtitle>
              <Flex mt={4} flexWrap="wrap" justifyContent="space-between">
                {dataFlowSteps.map((step, index) => (
                  <ProcessCard
                    key={index}
                    width={["100%", "100%", "32%"]}
                    onClick={() =>
                      setSelectedProcess(
                        selectedProcess === index ? null : index,
                      )
                    }
                  >
                    <Flex alignItems="center" mb={3}>
                      {step.icon}
                      <Box
                        ml={3}
                        fontFamily={HEADING_FONT}
                        fontSize={20}
                        fontWeight={600}
                        color={PURPLE_TEXT_COLOR}
                      >
                        {step.title}
                      </Box>
                    </Flex>
                    <Box
                      mt={2}
                      fontSize={16}
                      lineHeight="160%"
                      color={
                        selectedProcess === index
                          ? PURPLE_TEXT_COLOR
                          : "inherit"
                      }
                    >
                      {step.details}
                    </Box>
                  </ProcessCard>
                ))}
              </Flex>
            </Flex>
          </Container>
        </Box>

        <Box backgroundColor="#FFF" py={90}>
          <Container className="!max-w-marketing-content">
            <Flex flexDirection="column" alignItems="center">
              <StyledTitle>Data Privacy & Retention</StyledTitle>
              <StyledSubtitle>
                Enterprise-grade data management and compliance
              </StyledSubtitle>
              <Flex
                mt={4}
                flexWrap="wrap"
                justifyContent="space-between"
                width="100%"
              >
                <InfoCard width={["100%", "100%", "48%"]}>
                  <Flex alignItems="center" mb={4}>
                    <Icon icon="time" />
                    <Box
                      ml={3}
                      fontFamily={HEADING_FONT}
                      fontSize={24}
                      fontWeight={700}
                    >
                      Retention Policies
                    </Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>
                      Standard 30-day retention for deleted content
                    </Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>
                      Configurable retention for active content
                    </Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Automated purge processes</Box>
                  </Flex>
                </InfoCard>

                <InfoCard width={["100%", "100%", "48%"]}>
                  <Flex alignItems="center" mb={4}>
                    <Icon icon="form" />
                    <Box
                      ml={3}
                      fontFamily={HEADING_FONT}
                      fontSize={24}
                      fontWeight={700}
                    >
                      Compliance Measures
                    </Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Regular compliance audits</Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Data protection impact assessments</Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Comprehensive documentation</Box>
                  </Flex>
                </InfoCard>
              </Flex>
            </Flex>
          </Container>
        </Box>

        <Box backgroundColor="#F6F6FB" py={90}>
          <Container className="!max-w-marketing-content">
            <Flex flexDirection="column" alignItems="center">
              <StyledTitle>Administrative Controls</StyledTitle>
              <StyledSubtitle>
                Comprehensive management tools and monitoring
              </StyledSubtitle>
              <Flex
                mt={4}
                flexWrap="wrap"
                justifyContent="space-between"
                width="100%"
              >
                <ProcessCard width={["100%", "100%", "32%"]}>
                  <Flex alignItems="center" mb={3}>
                    <Icon icon="people" />
                    <Box
                      ml={3}
                      fontFamily={HEADING_FONT}
                      fontSize={20}
                      fontWeight={600}
                    >
                      Access Control
                    </Box>
                  </Flex>
                  <Box>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Workspace Administrator</Box>
                    </Flex>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Program Lead</Box>
                    </Flex>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Group Administrator</Box>
                    </Flex>
                  </Box>
                </ProcessCard>

                <ProcessCard width={["100%", "100%", "32%"]}>
                  <Flex alignItems="center" mb={3}>
                    <Icon icon="cog" />
                    <Box
                      ml={3}
                      fontFamily={HEADING_FONT}
                      fontSize={20}
                      fontWeight={600}
                    >
                      Configuration
                    </Box>
                  </Flex>
                  <Box>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Scenario Management</Box>
                    </Flex>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Scoring Criteria</Box>
                    </Flex>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>User Permissions</Box>
                    </Flex>
                  </Box>
                </ProcessCard>

                <ProcessCard width={["100%", "100%", "32%"]}>
                  <Flex alignItems="center" mb={3}>
                    <Icon icon="stopwatch" />
                    <Box
                      ml={3}
                      fontFamily={HEADING_FONT}
                      fontSize={20}
                      fontWeight={600}
                    >
                      Monitoring
                    </Box>
                  </Flex>
                  <Box>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Real-time Sessions</Box>
                    </Flex>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Performance Analytics</Box>
                    </Flex>
                    <Flex mb={2} alignItems="flex-start">
                      <Dot />
                      <Box flex={1}>Audit Logging</Box>
                    </Flex>
                  </Box>
                </ProcessCard>
              </Flex>
            </Flex>
          </Container>
        </Box>

        <Box backgroundColor="#F6F6FB" py={90}>
          <Container className="!max-w-marketing-content">
            <Flex flexDirection="column" alignItems="center">
              <StyledTitle>Technical Specifications</StyledTitle>
              <StyledSubtitle>
                Infrastructure and security details
              </StyledSubtitle>
              <Flex
                mt={4}
                flexWrap="wrap"
                justifyContent="space-between"
                width="100%"
              >
                <InfoCard width={["100%", "100%", "48%"]}>
                  <Flex alignItems="center" mb={4}>
                    <Icon icon="lock" />
                    <Box
                      ml={3}
                      fontFamily={HEADING_FONT}
                      fontSize={24}
                      fontWeight={700}
                    >
                      Security Measures
                    </Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Cloud security controls</Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Network segmentation</Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Regular security assessments</Box>
                  </Flex>
                </InfoCard>

                <InfoCard width={["100%", "100%", "48%"]}>
                  <Flex alignItems="center" mb={4}>
                    <Icon icon="database" />
                    <Box
                      ml={3}
                      fontFamily={HEADING_FONT}
                      fontSize={24}
                      fontWeight={700}
                    >
                      System Constraints
                    </Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Concurrent session limits</Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Storage and processing bounds</Box>
                  </Flex>
                  <Flex mb={3} alignItems="flex-start">
                    <Dot />
                    <Box flex={1}>Language support scope</Box>
                  </Flex>
                </InfoCard>
              </Flex>
            </Flex>
          </Container>
        </Box>

        <Box backgroundColor="#FFF" py={90}>
          <Container className="!max-w-marketing-content">
            <Flex flexDirection="column" alignItems="center">
              <StyledTitle>Other Questions</StyledTitle>
              <Box width="100%" mt={4}>
                <AccordionItem
                  title="How do you handle customer data?"
                  isOpen={openSections["data-handling"]}
                  onToggle={() => toggleSection("data-handling")}
                >
                  We maintain strict data handling practices with logical
                  isolation of all customer data in our secure cloud
                  infrastructure. Customer data is protected by strict access
                  controls, with access limited to essential personnel and
                  logged for security purposes. Our infrastructure is designed
                  to prevent any unauthorized access while ensuring your data
                  remains accessible and secure for your business needs. We do
                  not use customer data for training our AI models unless
                  explicitly opted in.
                </AccordionItem>

                <AccordionItem
                  title="How do you manage data retention and privacy?"
                  isOpen={openSections["data-retention"]}
                  onToggle={() => toggleSection("data-retention")}
                >
                  Our platform provides customer-configurable data retention
                  periods to meet your specific compliance and business needs.
                  When data reaches the end of its retention period, we employ
                  secure deletion processes. All data is logically isolated
                  between customers, and access is strictly controlled through
                  role-based permissions. Regular security audits and monitoring
                  ensure ongoing compliance with privacy requirements.
                </AccordionItem>

                <AccordionItem
                  title="What security certifications do you maintain?"
                  isOpen={openSections["security-certs"]}
                  onToggle={() => toggleSection("security-certs")}
                >
                  We are currently in the final stages of obtaining our SOC 2
                  Type II certification, expected in Q1 2025. We regularly
                  undergo third-party security assessments and maintain
                  compliance with industry security standards. Our security
                  program is built on industry best practices, and we
                  continuously update our controls to address emerging threats.
                </AccordionItem>

                <AccordionItem
                  title="How do you test and validate your AI systems?"
                  isOpen={openSections["ai-testing"]}
                  onToggle={() => toggleSection("ai-testing")}
                >
                  Our AI testing process incorporates multiple validation layers
                  including regular testing against predefined scenarios,
                  automated safety and consistency checks, and human review of
                  edge cases. We conduct regular performance testing and
                  scenario validation to ensure consistency and effectiveness.
                  Our team actively monitors AI interactions and maintains
                  robust safety measures. We employ comprehensive bias testing
                  and mitigation strategies throughout development to ensure
                  fair and equitable treatment across different user groups and
                  scenarios.
                </AccordionItem>

                <AccordionItem
                  title="How do you ensure responsible AI development?"
                  isOpen={openSections["responsible-ai"]}
                  onToggle={() => toggleSection("responsible-ai")}
                >
                  Our AI development process incorporates extensive testing for
                  bias, safety, and reliability. We maintain robust monitoring
                  systems and human oversight of our AI models. All models
                  undergo rigorous validation before deployment, including tests
                  for accuracy, fairness, and potential misuse. We regularly
                  update our models and safety measures based on ongoing
                  research and real-world performance data.
                </AccordionItem>

                <AccordionItem
                  title="What is your roleplay creation process?"
                  isOpen={openSections["creation-process"]}
                  onToggle={() => toggleSection("creation-process")}
                >
                  Our roleplay creation follows a structured process including
                  template-based scenario development, automated content
                  scanning, multi-stage review, and thorough testing. Each
                  scenario undergoes bias assessment and security validation
                  before deployment.
                </AccordionItem>

                <AccordionItem
                  title="How do you handle data retention and compliance?"
                  isOpen={openSections["data-retention-compliance"]}
                  onToggle={() => toggleSection("data-retention-compliance")}
                >
                  We maintain a standard 30-day retention period for deleted
                  content with configurable periods for active content. Our
                  compliance measures include regular audits, impact
                  assessments, and comprehensive documentation of all data
                  processing activities.
                </AccordionItem>

                <AccordionItem
                  title="What technical limitations should we be aware of?"
                  isOpen={openSections["tech-limits"]}
                  onToggle={() => toggleSection("tech-limits")}
                >
                  Our system has specific operational parameters to ensure
                  optimal performance. These include limits on concurrent
                  sessions per workspace, variable response times based on
                  complexity, and defined data storage limits. We provide
                  detailed documentation on these constraints and work with
                  enterprises to optimize their usage within these parameters.
                </AccordionItem>

                <AccordionItem
                  title="How do you ensure system reliability?"
                  isOpen={openSections["reliability"]}
                  onToggle={() => toggleSection("reliability")}
                >
                  We maintain comprehensive monitoring and testing procedures
                  including regular load testing, performance metrics tracking,
                  and automated system health checks. Our infrastructure is
                  designed for high availability with redundant systems and
                  automatic failover capabilities.
                </AccordionItem>
              </Box>
            </Flex>
          </Container>
        </Box>
      </Box>
      <PublicFooter />
    </>
  );
}
